import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Avatar } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getRealm } from "../../../../AuthProvider";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import Flex from "../../../../components/Flex";
import SearchInput from "../../../../components/SearchInput";
import Text from "../../../../components/Text";
import StyledButtonV2 from "../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../hooks/use-debounce";

import { PATIENT_PATHS } from "../../../../constants/paths";
import useDisclosure from "../../../../hooks/use-disclosure";
import { useProviderListV2 } from "../../../../queries/use-provider-list-v2";
import {
  DEFAULT_FONTSIZE,
  DEFAULT_USER_SRC,
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
} from "../../../../utils/utils";
import PatientSectionWrapper from "../PatientSectionWrapper";
import PatientPortalAppointMentModal from "../appointment/patientAppointmentForm/PatientPortalAppointMentModal";

const PatientProviders = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const [providerSearch, setProviderSearch, instantProviderSearch] =
    useDebounce("");

  const hostNameArr = location.hostname?.split(".");
  const xTENANTID = getRealm(hostNameArr);

  // Fetch providers
  const { data: providersList } = useProviderListV2({
    pagination,
    gridSorting,
    searchString: providerSearch || undefined,
    xTENANTID: xTENANTID ?? undefined,
    status: true,
    isReferral: false,
  });

  // Format contact info
  const formatContact = (provider: any) => {
    const contact = provider.phone || provider.contact || "-";
    const email = provider.email || "-";
    return { contact, email };
  };

  const patientPortalAppointmentDisclosure = useDisclosure();

  // Handle book appointment
  const handleBookAppointment = (row: any) => {
    const provider = { value: row?.uuid, label: row?.name };
    patientPortalAppointmentDisclosure.open(provider);
  };

  const COLUMNS = applyDefaultColumnSettings<any>([
    {
      field: "name",
      headerName: "Provider Name",
      flex: 1.2,
      minWidth: 280,
      disableColumnMenu: true,
      renderCell: ({ row, value }: any) => {
        const specialty = row?.specialities
          ? Object.values(row.specialities as Record<string, string>)[0]
          : undefined;
        return (
          <Flex gap={1.5} alignItems={"center"}>
            <Avatar
              src={row?.avatar || DEFAULT_USER_SRC}
              alt={value}
              sx={{ width: 36, height: 36 }}
            />
            <Flex flexDirection={"column"} gap={0.25}>
              <Text
                fontWeight={"medium"}
                fontSize={DEFAULT_FONTSIZE.fontSize}
                color={"primary.main"}
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  navigate(`${PATIENT_PATHS.PROVIDERS}/${row?.uuid}`)
                }
              >
                {value || "-"}
              </Text>
              {specialty && (
                <Text fontSize={"12px"} color={"text.secondary"}>
                  {specialty}
                </Text>
              )}
            </Flex>
          </Flex>
        );
      },
      sortable: false,
    },
    {
      field: "locationName",
      headerName: "Location",
      flex: 1.5,
      minWidth: 300,
      disableColumnMenu: true,
      renderCell: ({ value }: any) => {
        return value || "-";
        // const address = getAddressFormat(row?.physicalAddress)

        // return (
        // <Tooltip title={address}>
        //   <Text fontSize={DEFAULT_FONTSIZE.fontSize}>{address}</Text>
        // </Tooltip>

        // );
      },
      sortable: false,
    },
    {
      field: "contact",
      headerName: "Contact",
      flex: 1,
      minWidth: 250,
      disableColumnMenu: true,
      renderCell: ({ row }: any) => {
        const { contact, email } = formatContact(row);

        return (
          <Flex flexDirection={"column"} gap={0.5}>
            <Text fontSize={DEFAULT_FONTSIZE.fontSize}>{contact}</Text>

            <Text fontSize={DEFAULT_FONTSIZE.fontSize} color={"primary.main"}>
              {email}
            </Text>
          </Flex>
        );
      },
      sortable: false,
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.8,
      minWidth: 180,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      sortable: false,
      renderCell: ({ row }: any) => {
        return (
          <StyledButtonV2
            label={"Book Appointment"}
            variant="outlined"
            sIcon={<CalendarTodayIcon />}
            onClick={() => handleBookAppointment(row)}
          />
        );
      },
    },
  ]);

  return (
    <PatientSectionWrapper
      title="Providers"
      endAction={
        <Flex gap={2} alignItems={"center"}>
          {/* <Box width={"260px"}>
            <CustomSelect
              placeholder="All Specialty"
              options={specialtyOptions}
              value={selectedSpecialty || ""}
              onChange={(value) => setSelectedSpecialty(value ?? "")}
              onSearch={(_, val) => setSpecialtySearch(val)}
              clearable
            />
          </Box> */}
          {/* <Box width={"260px"}>
            <CustomSelect
              placeholder="All Provider"
              options={providerOptions}
              value={selectedProvider || ""}
              onChange={(value) => {
                setSelectedProvider(value ?? "");
                if (value) {
                  setProviderSearch(
                    providerOptions.find((opt) => opt.value === value)?.label ||
                    ""
                  );
                } else {
                  setProviderSearch("");
                }
              }}
              onSearch={(_, val) => setProviderSearch(val)}
              clearable
            />
          </Box> */}
          <SearchInput
            searchKey={instantProviderSearch}
            onSearch={(v) => setProviderSearch(v)}
            onClear={() => setProviderSearch("")}
            placeholder="Search by provider name"
          />
        </Flex>
      }
    >
      <Flex
        flex={1}
        width={"100%"}
        height={"100%"}
        minWidth={0}
        flexDirection={"column"}
        gap={2}
        sx={{ overflow: "hidden" }}
      >
        <PatientPortalAppointMentModal
          disclosureHost={patientPortalAppointmentDisclosure}
        />
        <CustomDataGridV2
          getRowId={(row) => row?.uuid}
          rows={providersList?.data?.content ?? []}
          rowCount={providersList?.data?.page?.totalElements ?? 0}
          columns={COLUMNS}
          disableRowSelectionOnClick
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          getRowHeight={() => "auto"}
          onSortModelChange={setGridSorting}
        />
      </Flex>
    </PatientSectionWrapper>
  );
};

export default PatientProviders;
